"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { useExchangeRates } from "@/hooks/use-exchange-rates";
import {
  formatRateInput,
  getCurrencyForCountry,
  parseRateInput,
  RATE_INPUT_PATTERN,
} from "@/lib/hourly-rate";
import { api } from "@/lib/trpc/react";

interface StoredRate {
  minimumHourlyRate: number | null;
  country: string | null;
}

/**
 * Drives the hourly-rate input. The settings form and the /join rate step both
 * edit the same column and both have to convert to and from the expert's local
 * currency around a USD store, so that lives here rather than in either one.
 *
 * Named for the `User.minimumHourlyRate` column it writes; the field is
 * presented to experts as their preferred rate.
 */
export function useMinimumHourlyRate() {
  const { data, isLoading } = api.userSettings.getUserSettings.useQuery();
  const {
    localToUsd,
    usdToLocal,
    rates,
    isLoading: ratesLoading,
  } = useExchangeRates();

  const stored = data as StoredRate | undefined;

  const [value, setValue] = useState("");
  // Whether the expert has edited the input. "Changed" is then a comparison
  // against the stored rate made per keystroke, never a function of whether
  // seeding happened: gating it on the seed meant a read that failed or an FX
  // table missing the expert's currency left the save permanently disabled, and
  // /join discarded the typed rate at "All set" without an error.
  const [touched, setTouched] = useState(false);
  const seeded = useRef(false);

  const { symbol: currencySymbol, code: currencyCode } = useMemo(
    () => getCurrencyForCountry(stored?.country),
    [stored?.country],
  );

  const isUsd = currencyCode === "USD";
  const hasRates = !isUsd && Boolean(rates?.[currencyCode]);
  // A settled table with no entry for this currency reads the same as a failed
  // one; `country-data-list` returns pre-redenomination codes (VEF, MRO, STD)
  // the feed carries no rate for. Only a table still in flight makes an expert
  // wait, otherwise the input would show a USD figure under a local symbol.
  const canDisplay = isUsd || hasRates || !ratesLoading;

  const storedValue = useMemo<string>(() => {
    const usd = stored?.minimumHourlyRate;
    if (usd == null) {
      return "";
    }
    // Round to a whole unit first: the value makes a round-trip through the FX
    // table on every save, and the cents would drift each time.
    return formatRateInput(
      String(hasRates ? Math.round(usdToLocal(usd, currencyCode)) : usd),
    );
  }, [stored?.minimumHourlyRate, hasRates, usdToLocal, currencyCode]);

  // The stored rate seeds the input exactly once, and never over an edit. Both
  // halves matter: a later refetch would overwrite what the expert has typed
  // since, and on a slow read the first response arrives *after* they have
  // started typing, so seeding then would wipe the value they are about to save.
  useEffect(() => {
    if (!stored || seeded.current || touched || !canDisplay) {
      return;
    }
    setValue(storedValue);
    seeded.current = true;
  }, [stored, touched, canDisplay, storedValue]);

  const parsed = parseRateInput(value);

  return {
    value,
    setValue: (next: string) => {
      if (next === "" || RATE_INPUT_PATTERN.test(next)) {
        setValue(next);
        setTouched(true);
      }
    },
    blur: () => {
      setValue((v) => formatRateInput(v));
    },
    /** USD, ready to send to userSettings.updateUserSettings. */
    payload: {
      minimumHourlyRate:
        parsed == null
          ? null
          : hasRates
            ? localToUsd(parsed, currencyCode)
            : parsed,
    },
    hasChanges: touched && formatRateInput(value) !== storedValue,
    isLoading,
    currencySymbol,
    currencyCode,
    /** Conversion is unavailable, so the input is showing raw USD. */
    showingUsdFallback: !isUsd && !hasRates && !ratesLoading,
    /**
     * Call after the save resolves. Clearing `touched` is enough: the input
     * already shows what was saved, and re-seeding it from the query instead
     * would read the pre-save cache and strip that value back out.
     */
    markSaved: () => {
      setValue((v) => formatRateInput(v));
      setTouched(false);
    },
  };
}
